/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: components\IVROptionEditor.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the PBX Incoming Call Tools module.
----------------------------------------------------------------*/

import { useState, useEffect } from 'react';
import { Edit, Trash2, Check, X } from 'lucide-react';
import { Input } from '../../../components/ui/input';
import Toggle from '../../../components/ui/Toggle';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select-shadcn';
import ModuleDestinationSelect from '../../../components/ui/ModuleDestinationSelect';
import { useI18n } from '../../../hooks/useI18n';
import { IVROption } from '../types/ivr.types';

// --- OptionEditor -----------------------------------------------------------

export interface OptionEditorProps {
  option: IVROption;
  index: number;
  isEditing: boolean;
  onEdit: () => void;
  onCancel: () => void;
  onSave: (option: IVROption) => void;
  onDelete: () => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  availableIVRs: any[];
  errors: Record<string, string>;
}

export function OptionEditor({
  option, index, isEditing, onEdit, onCancel, onSave, onDelete, availableIVRs, errors
}: OptionEditorProps) {
  const { t } = useI18n();
  const [localOption, setLocalOption] = useState<IVROption>(option);

  useEffect(() => { setLocalOption(option); }, [option]);

  const getDestinationText = () => {
    if (localOption.action === 'repeat') return t('ivrs:options.repeatMenu');
    if (!localOption.destination) return '-';
    if (localOption.action === 'goto') {
      const ivr = availableIVRs.find(ivr => `ivr:${ivr.context}` === localOption.destination);
      return ivr ? `${ivr.name} (${ivr.context})` : localOption.destination;
    }
    return localOption.destination;
  };

  if (!isEditing) {
    return (
      <div className="grid grid-cols-12 gap-4 items-center py-2 border-b">
        <div className="col-span-2 font-mono font-semibold">{localOption.digits}</div>
        <div className="col-span-8 text-sm">{getDestinationText()}</div>
        <div className="col-span-1 flex justify-center"><Toggle value={localOption.enabled} onChange={() => {}} disabled /></div>
        <div className="col-span-1 flex items-center justify-end gap-2">
          <button type="button" onClick={onEdit} className="p-1 hover:bg-muted rounded-md transition-colors"><Edit className="h-4 w-4" /></button>
          <button type="button" onClick={onDelete} className="p-1 hover:bg-muted rounded-md transition-colors text-red-500"><Trash2 className="h-4 w-4" /></button>
        </div>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-12 gap-4 items-center py-2 border-b">
      <div className="col-span-2">
        <Input value={localOption.digits} onChange={(e) => setLocalOption({ ...localOption, digits: e.target.value })}
          placeholder={t('ivrs:fields.optionDigits.placeholder')} className={errors[`option_${index}_digits`] ? 'border-red-500' : ''} />
        {errors[`option_${index}_digits`] && <p className="text-xs text-red-500 mt-1">{errors[`option_${index}_digits`]}</p>}
      </div>
      <div className="col-span-8">
        {localOption.action === 'repeat' ? (
          <div className="flex items-center gap-2 p-2 border rounded-md bg-muted/50">
            <span className="text-sm">{t('ivrs:options.repeatMenu')}</span>
            <button type="button" onClick={() => setLocalOption({ ...localOption, action: 'transfer', destination: '' })} className="text-xs text-muted-foreground hover:text-foreground">
              {t('ivrs:options.change')}
            </button>
          </div>
        ) : localOption.action === 'goto' ? (
          <Select value={localOption.destination} onValueChange={(value) => setLocalOption({ ...localOption, destination: value })}>
            <SelectTrigger className={errors[`option_${index}_destination`] ? 'border-red-500' : ''}>
              <SelectValue placeholder={t('ivrs:fields.optionDestination.placeholder')} />
            </SelectTrigger>
            <SelectContent>
              {availableIVRs.map((ivr) => (<SelectItem key={ivr.id} value={`ivr:${ivr.context}`}>{ivr.name} ({ivr.context})</SelectItem>))}
            </SelectContent>
          </Select>
        ) : (
          <ModuleDestinationSelect
            moduleValue={(localOption.settings?.moduleType as string) || ''}
            destinationValue={localOption.destination}
            onModuleChange={(value) => setLocalOption({ ...localOption, destination: '', settings: { ...localOption.settings, moduleType: value } })}
            onDestinationChange={(value) => setLocalOption({ ...localOption, destination: value })}
            destinationPlaceholder={t('ivrs:fields.optionDestination.placeholder')}
          />
        )}
        {localOption.action === 'goto' && errors[`option_${index}_destination`] && (
          <p className="text-xs text-red-500 mt-1">{errors[`option_${index}_destination`]}</p>
        )}
        {(errors[`option_${index}_action`] || errors[`option_${index}_destination`]) && localOption.action !== 'goto' && (
          <p className="text-xs text-red-500 mt-1">{errors[`option_${index}_destination`] || errors[`option_${index}_action`]}</p>
        )}
      </div>
      <div className="col-span-1 flex justify-center">
        <Toggle value={localOption.enabled} onChange={(checked) => setLocalOption({ ...localOption, enabled: checked })} />
      </div>
      <div className="col-span-1 flex items-center justify-end gap-2">
        <button type="button" onClick={() => onSave(localOption)} aria-label={t('ivrs:actions.save')} className="p-1 hover:bg-muted rounded-md transition-colors text-green-600"><Check className="h-4 w-4" /></button>
        <button type="button" onClick={onCancel} aria-label={t('ivrs:actions.cancel')} className="p-1 hover:bg-muted rounded-md transition-colors"><X className="h-4 w-4" /></button>
      </div>
    </div>
  );
}

// --- DestinationSelector ----------------------------------------------------

export interface DestinationSelectorProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  value: { action: string; destination: string; settings?: Record<string, any> } | null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onChange: (value: { action: string; destination: string; settings?: Record<string, any> } | null) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  availableIVRs: any[];
}

export function DestinationSelector({ value, onChange, availableIVRs }: DestinationSelectorProps) {
  const { t } = useI18n();
  const action = value?.action || 'transfer';
  const destination = value?.destination || '';

  if (action === 'goto') {
    return (
      <Select value={destination} onValueChange={(newDestination) => onChange({ action: 'goto', destination: newDestination })}>
        <SelectTrigger><SelectValue placeholder={t('ivrs:fields.optionDestination.placeholder')} /></SelectTrigger>
        <SelectContent>
          {availableIVRs.map((ivr) => (<SelectItem key={ivr.id} value={`ivr:${ivr.context}`}>{ivr.name} ({ivr.context})</SelectItem>))}
        </SelectContent>
      </Select>
    );
  }

  return (
    <ModuleDestinationSelect
      moduleValue={value?.settings?.moduleType || ''}
      destinationValue={destination}
      onModuleChange={(newModule) => onChange({ action: 'transfer', destination: '', settings: { ...value?.settings, moduleType: newModule } })}
      onDestinationChange={(newDestination) => {
        if (!newDestination) { onChange(null); return; }
        let newAction = 'transfer';
        if (newDestination.startsWith('ivr:')) newAction = 'goto';
        else if (newDestination.startsWith('voicemail:')) newAction = 'voicemail';
        onChange({ action: newAction, destination: newDestination, settings: value?.settings || {} });
      }}
      destinationPlaceholder={t('ivrs:fields.optionDestination.placeholder')}
    />
  );
}
